import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, ArrowRight, User2, Phone, AtSign, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API_BASE as API } from "@/config";
import axios from "axios";

export default function InfluencerRegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", upi_id: "", instagram: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.phone) return toast.error("Please fill all required fields");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      await axios.post(`${API}/influencer/register`, form);
      toast.success("Registration submitted! Awaiting admin approval.");
      navigate("/influencer/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { key: "name", label: "Full Name", icon: <User2 className="w-4 h-4" />, type: "text", placeholder: "Your name", required: true },
    { key: "email", label: "Email", icon: <Mail className="w-4 h-4" />, type: "email", placeholder: "you@example.com", required: true },
    { key: "phone", label: "Phone", icon: <Phone className="w-4 h-4" />, type: "tel", placeholder: "+91 XXXXXXXXXX", required: true },
    { key: "instagram", label: "Instagram (optional)", icon: <Instagram className="w-4 h-4" />, type: "text", placeholder: "@handle" },
    { key: "upi_id", label: "UPI ID (for payouts)", icon: <AtSign className="w-4 h-4" />, type: "text", placeholder: "name@upi" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
            <User2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Space Grotesk' }}>Become an Affiliate</h1>
          <p className="text-gray-400 text-sm">Earn commission on every referral sale</p>
        </div>

        <form onSubmit={handleRegister} className="bg-gray-900/60 backdrop-blur-xl rounded-3xl p-6 border border-gray-800 space-y-3">
          {fields.map(f => (
            <div key={f.key}>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 block">{f.label}</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{f.icon}</span>
                <Input data-testid={`inf-reg-${f.key}`} type={f.type} placeholder={f.placeholder}
                  value={form[f.key]} onChange={e => update(f.key, e.target.value)}
                  className="pl-10 bg-gray-800/50 border-gray-700 text-white rounded-xl focus:border-amber-500 focus:ring-amber-500/20" />
              </div>
            </div>
          ))}
          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 block">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input data-testid="inf-reg-password" type={showPw ? "text" : "password"} placeholder="Min 6 characters"
                value={form.password} onChange={e => update("password", e.target.value)}
                className="pl-10 pr-10 bg-gray-800/50 border-gray-700 text-white rounded-xl focus:border-amber-500 focus:ring-amber-500/20" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button data-testid="inf-register-btn" type="submit" disabled={loading}
            className="w-full bg-gradient-to-r from-amber-400 to-amber-600 text-gray-900 font-bold rounded-2xl py-6 text-base hover:from-amber-500 hover:to-amber-700 transition-all disabled:opacity-40 mt-2">
            {loading ? <span className="flex items-center gap-2"><span className="w-5 h-5 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin" />Submitting...</span>
              : <span className="flex items-center gap-2">Submit Application <ArrowRight className="w-5 h-5" /></span>}
          </Button>
        </form>

        <p className="text-center text-gray-500 text-sm mt-5">
          Already registered?{" "}
          <Link to="/influencer/login" className="text-amber-400 font-semibold hover:text-amber-300 transition-colors" data-testid="inf-login-link">Login here</Link>
        </p>
        <Link to="/" className="block text-center text-gray-600 text-xs mt-3 hover:text-gray-400 transition-colors">
          Back to VishuAstro
        </Link>
      </motion.div>
    </div>
  );
}
